import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
    setMobileCatOpen(false);
  }, [location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleShoppingGuide = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (location.pathname === '/') {
      const el = document.getElementById('categories');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById('categories');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 400);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 nav-blur border-b border-background-200/70'
          : 'bg-gradient-to-b from-foreground-950/50 via-foreground-950/25 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 no-underline"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-table-line text-lg"></i>
            </div>
            <span className={`text-lg font-bold tracking-tight whitespace-nowrap font-heading transition-colors duration-300 ${
              scrolled ? 'text-foreground-950' : 'text-background-50'
            }`}>
              Itaobuy<span className={scrolled ? 'text-primary-500' : 'text-primary-300'}>Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
                  : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              Home
            </Link>

            <a
              href="#categories"
              onClick={handleShoppingGuide}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
                  : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              Shopping Guide
            </a>

            {/* Categories Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <button
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-1 ${
                  scrolled
                    ? 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-background-50 rounded-xl border border-background-200/70 nav-blur py-2 shadow-sm max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                        loading="lazy"
                      />
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/article/what-is-itaobuyspreadsheet-complete-guide"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
                  : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Itaobuy Sheet
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              scrolled
                ? 'text-foreground-700 hover:bg-background-100'
                : 'text-background-50 hover:bg-background-50/10'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link
              to="/"
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors"
            >
              <i className="ri-home-line mr-2"></i>
              Home
            </Link>

            <a
              href="#categories"
              onClick={handleShoppingGuide}
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors"
            >
              <i className="ri-guide-line mr-2"></i>
              Shopping Guide
            </a>

            {/* Mobile Categories Accordion */}
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line"></i>
                Browse Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="ml-2 border-l-2 border-background-200 pl-2 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors"
                  >
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                      loading="lazy"
                    />
                    <span className="whitespace-nowrap">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-200/70 my-1"></div>

            <Link
              to="/article/what-is-itaobuyspreadsheet-complete-guide"
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors"
            >
              <i className="ri-book-open-line mr-2"></i>
              Complete Guide
            </Link>

            {/* Mobile Article Links */}
            <div className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
              More Guides
            </div>
            <Link to="/article/how-to-use-itaobuy-spreadsheet" className="px-4 py-2 text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors">How to Use Guide</Link>
            <Link to="/article/itaobuyspreadsheet-vs-traditional-shopping" className="px-4 py-2 text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors">vs Traditional Shopping</Link>
            <Link to="/article/itaobuyspreadsheet-safety-transparency" className="px-4 py-2 text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors">Safety Report</Link>
            <Link to="/article/smart-shopping-spreadsheets-2026" className="px-4 py-2 text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors">Smart Shopping 2026</Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 px-4 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors text-center whitespace-nowrap"
            >
              <i className="ri-rocket-line mr-2"></i>
              Itaobuy Sheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}